import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import { useMutation } from '@apollo/client';
import { sanitizeString } from 'utils/xss';
import PropTypes from 'prop-types';

import { ADD_TAG, UPDATE_TAG, UPDATE_TAG_ORDER, DELETE_TAG } from 'graphql/mutations/tagsMutation';
import ManageTags from 'pages/Dashboard/components/ProposalItem/components/ManageTags';
import CreateTags from 'pages/Dashboard/components/ProposalItem/components/CreateTags';

import 'pages/Dashboard/components/ProposalItem/components/ProposalItemTag.scss';

import './BulkTagsPopover.scss';

/**
 * Bulk tags popover
 * @param {string} mode - Mode
 * @param {array} tags - Tags
 * @param {array} initialSelectedTagIds - Initial selected tag IDs
 * @param {function} onCancel - On cancel
 * @param {function} onConfirm - On confirm
 * @param {function} fetchTags - Fetch tags
 */
const BulkTagsPopover = ({ mode, tags, initialSelectedTagIds, onCancel, onConfirm, fetchTags }) => {
  const [selectedTagIds, setSelectedTagIds] = useState(initialSelectedTagIds || []);
  const [tagsData, setTagsData] = useState(tags ?? []);
  const [searchText, setSearchText] = useState('');
  const [searchedChecked, setSearchedChecked] = useState([]);
  const [searchedUnchecked, setSearchedUnchecked] = useState([]);
  const [searchedAlltags, setSearchedAlltags] = useState([]);
  const [editTags, setEditTags] = useState(false);
  const [selectedTagColor, setSelectedTagColor] = useState(false);
  const [openDeletePopover, setOpenDeletePopover] = useState(false);
  const [createTag, setCreateTag] = useState(false);
  const [tagsLoader, setTagsLoader] = useState(false);

  useEffect(() => {
    setSelectedTagIds(initialSelectedTagIds ?? []);
    setSearchText('');
  }, [initialSelectedTagIds, mode]);

  useEffect(() => {
    setTagsData(tags ?? []);
  }, [tags]);

  const allCheckedTags = (tagsData ?? []).filter(
    (t) => t?._id !== 0 && selectedTagIds.includes(t._id)
  );
  const allUncheckedTags = (tagsData ?? []).filter(
    (t) => t?._id !== 0 && !selectedTagIds.includes(t._id)
  );

  /**
   * Handle add tag mutation
   */
  const [addTag] = useMutation(ADD_TAG, {
    onCompleted: ({ addTag }) => {
      setTagsLoader(false);
      setCreateTag(false);
      setSearchText('');

      // add the tag to tags data
      const newTagsData = [...tagsData, addTag];
      setTagsData(newTagsData);

      fetchTags();
    },
    onError: (err) => {
      setTagsLoader(false);
      setCreateTag(false);
      setSearchText('');
      console.error('addTag error', err);
    },
  });

  /**
   * Handle update tag mutation
   */
  const [updateTag] = useMutation(UPDATE_TAG, {
    onCompleted: () => {
      fetchTags();
    },
    onError: (err) => {
      console.error('updateTag error', err);
    },
  });

  /**
   * Handle update tags order mutation
   */
  const [updateTagsOrder] = useMutation(UPDATE_TAG_ORDER, {
    onCompleted: () => {
      fetchTags();
    },
    onError: (err) => {
      console.error('updateTagsOrder error', err);
    },
  });

  /**
   * Handle delete tag mutation
   */
  const [deleteTagMutation] = useMutation(DELETE_TAG, {
    onCompleted: () => {
      fetchTags();
    },
    onError: (err) => {
      console.error('deleteTag error', err);
    },
  });

  /**
   * Handle tag checked
   * @param {string} tagId - Tag ID
   * @param {boolean} checked - Checked
   * @returns {void}
   */
  const handleTagChecked = (tagId, checked) => {
    setSelectedTagIds((prev) => (checked ? [...prev, tagId] : prev?.filter((id) => id !== tagId)));
  };

  /**
   * Handle add or remove tags
   * @returns {void}
   */
  const handleAddOrRemoveTag = () => {
    if (mode === 'add') {
      // Pass all selected tags so every selected proposal gets every selected tag
      // (Dashboard only adds tags that are not already on each proposal)
      onConfirm(selectedTagIds ?? [], []);
    } else {
      onConfirm([], selectedTagIds);
    }
    setSelectedTagIds([]);
  };

  /**
   * Handle cancel
   * @returns {void}
   */
  const handleCancel = () => {
    setSelectedTagIds([]);
    onCancel();
  };

  /**
   * Handle edit tag (name, color, type) and persist via UPDATE_TAG
   * @param {string} tagId - Tag ID
   * @param {string} name - Name
   * @param {string} color - Color
   * @param {string} type - Type
   * @returns {void}
   */
  const handleEditTag = (tagId, name, color, type) => {
    if (!tagId) return;
    const index = tagsData.findIndex((tag) => tag._id === tagId);
    if (index < 0) return;

    const current = tagsData[index];
    const newData = { ...current, _id: String(current._id) };
    if (color != null && type != null) {
      newData.color = color;
      newData.type = type;
    }
    if (name != null) {
      const trimmed = sanitizeString(String(name).trim());
      if (trimmed) newData.name = trimmed;
    }

    updateTag({
      variables: {
        _id: String(newData._id),
        name: newData.name ?? '',
        color: newData.color ?? '',
        type: newData.type ?? '',
      },
    });

    setTagsData((prev) => {
      const next = [...prev];
      next[index] = { ...newData };
      return next;
    });

    if (searchedAlltags?.length) {
      setSearchedAlltags((prev) => prev.map((tag) => (tag._id === tagId ? { ...newData } : tag)));
    }
  };

  /**
   * Delete tag from account and remove from local state and selection
   * @param {string} tagId - Tag ID
   */
  const handleDeleteTag = (tagId) => {
    setOpenDeletePopover('');
    deleteTagMutation({ variables: { _id: String(tagId) } });
    setTagsData((prev) => prev.filter((t) => t._id !== tagId));
    setSelectedTagIds((prev) => prev.filter((id) => id !== tagId));
    setSearchedAlltags((prev) => (prev ?? []).filter((t) => t._id !== tagId));
  };

  /**
   * Handle drag end: persist new order via UPDATE_TAG_ORDER (useDnd already updates tagsData via setTagsData)
   * @param {array} updatedTags - Updated tags
   * @returns {void}
   */
  const handleDragEnd = (updatedTags) => {
    if (!updatedTags?.length) return;
    const tagIds = updatedTags.map((t) => String(t._id));
    updateTagsOrder({ variables: { tagIds } });
  };

  /**
   * Check if a tag with same name, color and type already exists (for create/edit validation)
   * @param {string} tagName - Tag name
   * @param {string} color - Color
   * @param {string} tagType - Tag type
   * @param {string} excludeId - Exclude ID
   * @returns {boolean} - Is duplicate
   * @returns {boolean} - Is duplicate
   */
  const isDuplicate = (tagName, color, tagType, excludeId = null) => {
    return (tagsData ?? []).some(
      (t) =>
        t.name === tagName &&
        t.color === color &&
        t.type === tagType &&
        (excludeId == null || t._id !== excludeId)
    );
  };

  /**
   * Handle create tag
   * @param {string} tagName - Tag name
   * @param {string} color - Color
   * @param {string} tagType - Tag type
   * @returns {boolean} - Is duplicate
   */
  const createTagHandle = (tagName, color, tagType) => {
    tagName = sanitizeString(tagName.trim());

    if (!tagName.length || !color || !tagType) {
      return;
    }

    setTagsLoader(true);

    addTag({
      variables: {
        name: tagName,
        color: color,
        type: tagType,
      },
    });
  };

  /**
   * Render bulk tags popover
   * @returns {React.ReactNode} - Bulk tags popover content
   */
  return (
    <div className="bulk-tags-popover-content" onClick={(e) => e.stopPropagation()}>
      {createTag || createTag === '' ? (
        <CreateTags
          isDuplicate={isDuplicate}
          createTag={createTag}
          setCreateTag={setCreateTag}
          createTagHandle={createTagHandle}
          tagsLoader={tagsLoader}
        />
      ) : (
        <ManageTags
          visibleTags={true}
          allCheckedTags={allCheckedTags}
          allUncheckedTags={allUncheckedTags}
          editTags={editTags}
          setEditTags={setEditTags}
          handleEditTag={handleEditTag}
          selectedTagColor={selectedTagColor}
          setSelectedTagColor={setSelectedTagColor}
          deleteTag={handleDeleteTag}
          openDeletePopover={openDeletePopover}
          setOpenDeletePopover={setOpenDeletePopover}
          searchText={searchText}
          setSearchText={setSearchText}
          searchedChecked={searchedChecked}
          setSearchedChecked={setSearchedChecked}
          searchedUnchecked={searchedUnchecked}
          setSearchedUnchecked={setSearchedUnchecked}
          searchedAlltags={searchedAlltags}
          setSearchedAlltags={setSearchedAlltags}
          setCreateTag={setCreateTag}
          handleTagChecked={handleTagChecked}
          onDragEnd={handleDragEnd}
          tagsData={tagsData}
          setTagsData={setTagsData}
          isDuplicate={isDuplicate}
          tagModeType={mode}
        />
      )}
      <div className="bulk-tags-popover-footer">
        <Button className="medium-btn grey-btn" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          className="medium-btn secondary-btn"
          onClick={handleAddOrRemoveTag}
          disabled={selectedTagIds.length === 0}>
          {mode === 'add' ? 'Add tags' : 'Remove tags'}
        </Button>
      </div>
    </div>
  );
};

/**
 * Bulk tags popover prop types
 */
BulkTagsPopover.propTypes = {
  mode: PropTypes.oneOf(['add', 'remove']).isRequired,
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      color: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  initialSelectedTagIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  fetchTags: PropTypes.func.isRequired,
};

/**
 * Bulk tags popover default props
 */
BulkTagsPopover.defaultProps = {
  mode: 'add',
  tags: [],
  initialSelectedTagIds: [],
  onCancel: () => {},
  onConfirm: () => {},
  fetchTags: () => {},
};

export default BulkTagsPopover;
